<template>
  <div
    ref="root"
    class="sp-slider"
    :class="classModifiers"
    :style="layoutStyle"
    v-bind="regionAttributes"
    @keydown="handleKeydown"
  >
    <sp-button
      v-if="showArrows"
      class="sp-slider__arrow sp-slider__previous"
      variant="outlined"
      icon="chevron-left-filled"
      :accessible-name="tAccessibleName('slider_previous')"
      :disabled="!canPrevious"
      @click="previous"
    ></sp-button>
    <div ref="container" class="sp-slider__container">
      <slot @slotchange="handleSlotChange" />
    </div>
    <sp-button
      v-if="showArrows"
      class="sp-slider__arrow sp-slider__next"
      variant="outlined"
      icon="chevron-right-filled"
      :accessible-name="tAccessibleName('slider_next')"
      :disabled="!canNext"
      @click="next"
    ></sp-button>
    <div v-if="showDots" class="sp-slider__dots">
      <span v-for="index in positions" :key="index" class="sp-slider__dot-cell">
        <sp-button
          class="sp-slider__dot"
          :class="dotModifiers(index)"
          variant="text"
          icon="ellipse-filled"
          size="xx-small"
          densitysmcompact="false"
          :accessible-name="dotName(index)"
          :aria-current="dotCurrent(index)"
          @click="goTo(index)"
        ></sp-button>
        <span class="sp-slider__dot-mark" aria-hidden="true"></span>
      </span>
    </div>
    <span v-if="carousel" class="sp-slider__status" aria-live="polite" aria-atomic="true">{{ status }}</span>
  </div>
</template>

<script setup>
import { useEventListener, useMutationObserver, whenever } from "@vueuse/core";
import { computed, nextTick, onBeforeUnmount, ref, useTemplateRef, watch } from "vue";
import { useExpose } from "#ce/composables/useExpose";
import { useHostElement } from "#ce/composables/useHostElement";
import { deepActiveElement, focusWithin } from "#ce/utils/focus";
import { tAccessibleName } from "#ce/shared/components-i18n";
import { toBoolean, toNumber } from "#ce/utils/props";
import { getSlot } from "#ce/utils/slots";
import { props as sliderProps } from "./props.js";
import { useSliderOptions } from "./composables/useSliderOptions";

const props = defineProps(sliderProps);
const emit = defineEmits(["update-selected-index", "scrollend"]);
const root = useTemplateRef("root");
const container = useTemplateRef("container");
const host = useHostElement();
const defaultSlot = computed(() => getSlot(container.value));
const items = ref([]);
const selectedIndex = ref(props.selectedIndex);
const options = useSliderOptions(root, props);
const infinite = computed(() => toBoolean(props.infinite));
const perView = computed(() => Math.min(options.value.slidesPerView ?? 1, Math.max(1, items.value.length)));
const lastIndex = computed(() =>
  Math.max(0, items.value.length - (options.value.slidesPerView === undefined ? 1 : perView.value)),
);
const canNavigate = computed(() => items.value.length > perView.value);
const canPrevious = computed(() => canNavigate.value && (infinite.value || selectedIndex.value > 0));
const canNext = computed(() => canNavigate.value && (infinite.value || selectedIndex.value < lastIndex.value));
const showArrows = computed(() => options.value.arrows && canNavigate.value);
const carousel = computed(
  () =>
    Boolean(props.regionLabel) ||
    infinite.value ||
    options.value.slidesPerView !== undefined ||
    options.value.arrows ||
    toBoolean(props.dots),
);
const showDots = computed(() => toBoolean(props.dots) && canNavigate.value);
const regionAttributes = computed(() =>
  carousel.value
    ? {
        role: "region",
        "aria-label": props.regionLabel || tAccessibleName("slider_default_region"),
        "aria-roledescription": tAccessibleName("slider_carousel"),
        tabindex: 0,
      }
    : {},
);
function dotModifiers(index) {
  return { "--is-current": currentPosition.value === index };
}
function dotName(index) {
  return tAccessibleName("slider_go_to", { slide: index + 1 });
}
function dotCurrent(index) {
  return currentPosition.value === index ? "true" : undefined;
}
// The starts the arrows reach, one dot each. Looping by a stride of 2 over 5 slides reaches 0, 2, 4, 1 and 3; a finite
// slider stops at its last full window.
const positions = computed(() => {
  const count = items.value.length;
  const stride = options.value.slidesToScroll;
  if (infinite.value) {
    const reached = new Set();
    for (let index = 0; count && !reached.has(index); index = (index + stride) % count) {
      reached.add(index);
    }
    return [...reached].sort((a, b) => a - b);
  }
  const end = lastIndex.value;
  const indices = [];
  for (let index = 0; index <= end; index += stride) {
    indices.push(index);
  }
  if (indices.at(-1) !== end) {
    indices.push(end);
  }
  return indices;
});
const currentPosition = computed(() => positions.value.findLast((index) => index <= selectedIndex.value));
const status = computed(() =>
  items.value.length
    ? tAccessibleName("slider_status", { slide: selectedIndex.value + 1, count: items.value.length })
    : "",
);
const classModifiers = computed(() => ({ "--is-carousel": carousel.value, "--has-arrows": showArrows.value }));
const layoutStyle = computed(() =>
  options.value.slidesPerView === undefined ? undefined : { "--slides-per-page": perView.value },
);
const originalPresentation = new Map();
let initialized = false;
let unmounted = false;
const pendingMovements = new Set();
const { exposeMethods } = useExpose();
exposeMethods({ previous, next });

whenever(defaultSlot, handleSlotChange, { once: true });
const { takeRecords } = useMutationObserver(host, handleHostMutations, { childList: true });
useEventListener(container, "scrollend", handleScrollEnd);

function restoreSlide(slide) {
  const original = originalPresentation.get(slide);
  if (!original) {
    return;
  }
  for (const [name, value] of Object.entries(original.attributes)) {
    if (value === null) {
      slide.removeAttribute(name);
    } else {
      slide.setAttribute(name, value);
    }
  }
  originalPresentation.delete(slide);
}

function updatePresentation() {
  if (unmounted) {
    return;
  }
  for (const slide of originalPresentation.keys()) {
    if (!items.value.includes(slide) || !carousel.value) {
      restoreSlide(slide);
    }
  }
  if (!carousel.value) {
    arrangeSlides();
    return;
  }
  items.value.forEach((slide, index) => {
    if (!originalPresentation.has(slide)) {
      originalPresentation.set(slide, {
        attributes: Object.fromEntries(
          ["role", "aria-roledescription", "aria-label", "aria-hidden", "inert"].map((name) => [
            name,
            slide.getAttribute(name),
          ]),
        ),
      });
    }
    const original = originalPresentation.get(slide);
    const distance = infinite.value
      ? (index - selectedIndex.value + items.value.length) % items.value.length
      : index - selectedIndex.value;
    const visible = distance >= 0 && distance < perView.value;
    slide.setAttribute("role", "group");
    slide.setAttribute("aria-roledescription", tAccessibleName("slider_slide"));
    slide.setAttribute(
      "aria-label",
      original.attributes["aria-label"] ||
        tAccessibleName("slider_status", { slide: index + 1, count: items.value.length }),
    );
    slide.setAttribute("aria-hidden", String(!visible));
    if (!visible || original.attributes.inert !== null) {
      slide.setAttribute("inert", "");
    } else {
      slide.removeAttribute("inert");
    }
  });
  arrangeSlides();
}

function shownOrder() {
  if (!infinite.value || !carousel.value) {
    return items.value;
  }
  return [...items.value.slice(selectedIndex.value), ...items.value.slice(0, selectedIndex.value)];
}

function moveToEnd(slide) {
  if (host.moveBefore) {
    host.moveBefore(slide, null);
  } else {
    host.append(slide);
  }
}

// A looping window is the slides' DOM order, so tab and reading order follow what it shows. `items` keeps the
// author's order. A move drops focus where `moveBefore` is missing, so the focused element is refocused.
function arrangeSlides() {
  syncAuthorChanges();
  const order = shownOrder();
  const current = hostSlides();
  if (order.length === current.length && order.every((slide, index) => slide === current[index])) {
    return;
  }
  const focused = deepActiveElement();
  const refocus = items.value.some(focusWithin) ? focused : null;
  order.forEach(moveToEnd);
  takeRecords();
  if (refocus && deepActiveElement() !== refocus) {
    refocus.focus({ preventScroll: true });
  }
}

// Applies the author's child changes that have not reached `items` yet, so a move never runs on a stale list.
// A slotted slide `items` still lacks joins it last, so the shown order always covers every slide.
function syncAuthorChanges() {
  const pending = takeRecords();
  if (pending.length) {
    items.value = replayAuthorChanges(items.value, pending);
  }
  const missing = hostSlides().filter((slide) => !items.value.includes(slide));
  if (missing.length) {
    items.value = [...items.value, ...missing];
  }
}

// Replays the author's own changes on `slides`: an inserted slide lands before the slide it was inserted before,
// or last when appended, which is the order the author meant whatever order the slides were shown in.
function replayAuthorChanges(slides, records) {
  const order = [...slides];
  for (const record of records) {
    for (const node of record.removedNodes) {
      const index = order.indexOf(node);
      if (index >= 0) {
        order.splice(index, 1);
      }
    }
    for (const node of record.addedNodes) {
      if (node.nodeType !== Node.ELEMENT_NODE || node.hasAttribute("slot") || node.parentNode !== host) {
        continue;
      }
      let anchor = record.nextSibling;
      while (anchor && !order.includes(anchor)) {
        anchor = anchor.nextSibling;
      }
      const at = anchor ? order.indexOf(anchor) : order.length;
      order.splice(at, 0, node);
    }
  }
  return order.filter((slide) => slide.parentNode === host);
}

function handleHostMutations(records) {
  if (!initialized) {
    return;
  }
  items.value = replayAuthorChanges(items.value, records);
  selectedIndex.value = normalizeIndex(selectedIndex.value);
  updatePresentation();
  slideIntoView();
}

function hostSlides() {
  return Array.from(host?.children ?? []).filter((element) => !element.hasAttribute("slot"));
}

function handleSlotChange() {
  if (!initialized) {
    items.value = hostSlides();
    initialized = true;
  }
  selectedIndex.value = normalizeIndex(selectedIndex.value);
  updatePresentation();
  slideIntoView();
}

function normalizeIndex(value) {
  const number = toNumber(value);
  const index = Number.isFinite(number) ? Math.trunc(number) : 0;
  if (!canNavigate.value) {
    return 0;
  }
  if (infinite.value) {
    return ((index % items.value.length) + items.value.length) % items.value.length;
  }
  return Math.max(0, Math.min(index, lastIndex.value));
}

watch(
  () => props.selectedIndex,
  (index) => {
    if (initialized) {
      syncAuthorChanges();
    }
    selectedIndex.value = normalizeIndex(index);
  },
);
watch([options, infinite, carousel], () => {
  selectedIndex.value = normalizeIndex(selectedIndex.value);
  updatePresentation();
  slideIntoView();
});
watch(selectedIndex, (index) => {
  updatePresentation();
  slideIntoView();
  emit("update-selected-index", index);
});

function handleScrollEnd() {
  for (const resolve of pendingMovements) {
    resolve();
  }
  pendingMovements.clear();
  emit("scrollend");
}

// A looping slider steps by the stride around the wrap. A finite one steps through the dot positions and stops at
// the ends, so the current dot always names the visible window.
function stepPosition(direction) {
  const count = items.value.length;
  if (!count) {
    return selectedIndex.value;
  }
  if (infinite.value) {
    return (((selectedIndex.value + direction * options.value.slidesToScroll) % count) + count) % count;
  }
  const list = positions.value;
  const target = list.indexOf(currentPosition.value) + direction;
  return list[Math.max(0, Math.min(target, list.length - 1))];
}

function previous() {
  return goTo(stepPosition(-1));
}
function next() {
  return goTo(stepPosition(1));
}

async function goTo(index) {
  const nextIndex = normalizeIndex(index);
  if (nextIndex === selectedIndex.value) {
    return;
  }
  selectedIndex.value = nextIndex;
  if (infinite.value) {
    await nextTick();
    handleScrollEnd();
    return;
  }
  return new Promise((resolve) => pendingMovements.add(resolve));
}

function slideIntoView() {
  const slide = items.value[selectedIndex.value];
  if (!slide) {
    return;
  }
  if (!container.value) {
    return;
  }
  if (infinite.value) {
    container.value.scrollLeft = 0;
  } else if (carousel.value && container.value.scrollTo) {
    const viewport = container.value;
    const padding = parseFloat(getComputedStyle(viewport).scrollPaddingInlineStart) || 0;
    const left =
      viewport.scrollLeft + slide.getBoundingClientRect().left - viewport.getBoundingClientRect().left - padding;
    viewport.scrollTo({ left, behavior: "smooth" });
    if (Math.abs(viewport.scrollLeft - left) < 0.5 && pendingMovements.size) {
      nextTick(handleScrollEnd);
    }
  } else {
    slide.scrollIntoView({ behavior: "smooth", block: "end", inline: "start" });
  }
}

function handleKeydown(event) {
  if (!carousel.value || event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) {
    return;
  }
  if (
    event
      .composedPath()
      .some((element) =>
        element.matches?.(
          'input, textarea, select, [contenteditable]:not([contenteditable="false"]), [role="textbox"], [role="combobox"]',
        ),
      )
  ) {
    return;
  }
  if (event.key === "ArrowLeft") {
    previous();
  } else if (event.key === "ArrowRight") {
    next();
  } else if (event.key === "Home") {
    goTo(0);
  } else if (event.key === "End") {
    goTo(infinite.value ? items.value.length - 1 : lastIndex.value);
  } else {
    return;
  }
  event.preventDefault();
}

onBeforeUnmount(() => {
  unmounted = true;
  for (const slide of originalPresentation.keys()) {
    restoreSlide(slide);
  }
  if (host?.isConnected) {
    items.value.forEach(moveToEnd);
    takeRecords();
  }
  for (const resolve of pendingMovements) {
    resolve();
  }
  pendingMovements.clear();
});
</script>

<style>
:host {
  --slides-per-page: 1;
  --aspect-ratio: 0;
  --slide-gap: var(--sp-comp-slider-gap, var(--sp-comp-slider-slide-gab, 1rem));
  --scroll-hint: var(--sp-comp-slider-scroll-hint, 0px);
  display: flex;
  box-sizing: border-box;
}
</style>

<style scoped lang="scss">
.sp-slider {
  position: relative;
  overflow: hidden;
  width: 100%;

  &.--has-arrows {
    --arrow-track: var(--sp-comp-slider-arrow-track-size, auto);

    display: grid;
    grid-template-columns: var(--arrow-track) minmax(0, 1fr) var(--arrow-track);
    align-items: center;
    gap: var(--sp-comp-slider-controls-gap, var(--sp-ref-scale-16));
  }
}

.sp-slider__container {
  --slide-size: calc((100% - (var(--slides-per-page) - 1) * var(--slide-gap)) / var(--slides-per-page));
  box-sizing: border-box;
  grid-area: slides;
  display: grid;
  height: 100%;
  width: 100%;
  grid-auto-flow: column;
  grid-auto-columns: var(--slide-size);
  grid-auto-rows: 100%;
  column-gap: var(--slide-gap);
  scroll-snap-type: x mandatory;
  scroll-padding-inline: var(--scroll-hint);
  padding-inline: var(--scroll-hint);
  place-items: center;
  overflow: hidden;
  overscroll-behavior-x: contain;
  scrollbar-width: none;
  aspect-ratio: calc(var(--aspect-ratio) * var(--slides-per-page));
  transition: aspect-ratio 5s ease-in-out;
}

.sp-slider.--has-arrows .sp-slider__container {
  grid-area: auto;
  min-width: 0;
}

.sp-slider__arrow {
  visibility: var(--sp-comp-slider-arrow-visibility, visible);

  --sp-comp-button-border-color: var(--sp-comp-slider-arrow-border-color, var(--sp-sys-color-primary));
  --sp-comp-button-text-color: var(--sp-comp-slider-arrow-color, var(--sp-sys-color-primary));
  --sp-comp-button-bg-color: var(--sp-comp-slider-arrow-bg-color, transparent);
  --sp-comp-button-border-radius: var(--sp-comp-slider-arrow-border-radius, var(--sp-sys-shape-control));
}

.sp-slider__dots {
  grid-column: 1 / -1;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--sp-comp-slider-dot-gap, var(--sp-ref-scale-4));
  min-height: var(--sp-comp-slider-dots-min-height, auto);
  margin-top: var(--sp-comp-slider-dots-margin-top, var(--sp-comp-slider-controls-gap, var(--sp-ref-scale-16)));
}

.sp-slider__previous {
  justify-self: start;
  margin-inline-start: var(--sp-comp-slider-arrow-offset, 0);
}

.sp-slider__next {
  justify-self: end;
  margin-inline-end: var(--sp-comp-slider-arrow-offset, 0);
}

// A dot is a filled icon. A host that sets `--sp-comp-slider-dot-mark-display` draws a ring mark over it
// instead, filled when current; it then hides the icon through the dot colours.
.sp-slider__dot-cell {
  display: var(--sp-comp-slider-dot-cell-display, contents);
  place-items: center;
}

.sp-slider__dot {
  --sp-comp-button-icon-fill-color: var(--sp-comp-slider-dot-color, var(--sp-sys-color-outline));
  --sp-comp-button-bg-color: transparent;
  --sp-comp-button-xx-small-height: var(--sp-comp-slider-dot-target-size);

  grid-area: 1 / 1;

  &.--is-current {
    --sp-comp-button-icon-fill-color: var(--sp-comp-slider-dot-active-color, var(--sp-sys-color-primary));
  }

  &::part(icon) {
    --size: var(--sp-comp-slider-dot-size, var(--sp-ref-scale-10));
  }
}

.sp-slider__dot-mark {
  display: var(--sp-comp-slider-dot-mark-display, none);
  grid-area: 1 / 1;
  box-sizing: border-box;
  width: var(--sp-comp-slider-dot-mark-size, var(--sp-ref-scale-12));
  height: var(--sp-comp-slider-dot-mark-size, var(--sp-ref-scale-12));
  border: 1px solid var(--sp-comp-slider-dot-mark-color, var(--sp-sys-color-outline));
  border-radius: 50%;
  pointer-events: none;

  .--is-current + & {
    background-color: var(--sp-comp-slider-dot-mark-active-color, var(--sp-sys-color-primary));
    border-color: var(--sp-comp-slider-dot-mark-active-color, var(--sp-sys-color-primary));
  }
}

.sp-slider__status {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}
</style>
